import { ref, computed, type Ref } from 'vue';
import { getActivePinia } from 'pinia';
import { useStrapiGet } from 'src/shared/composables/useStrapiApi';
import { Step } from 'src/modules/Step/models/Step';
import type { Item } from 'pinia-orm';
import { useRepo } from 'pinia-orm';
import { componentNameKebabCase } from 'shared/composables/strapi/useComponent';
import type {
  StepStepType,
  StepTypeComponnet,
  StepTypeKebabCase,
} from 'types/course/step';
import { UNIVERSAL_STEP_PARAMS } from './stepStrapiApi';
import { executeWithRetry, isRetryableError } from 'shared/utilities/apiRetry';
import { validateStepContent } from 'src/modules/Step/utilities/stepContentValidator';
import type { FilterableListItem } from 'shared/composables/useListFilters/useListFilters';

const stepRepo = useRepo(Step);

export function stepComponentFromStepType(
  stepType: StepStepType[] | undefined,
): StepTypeComponnet | null {
  return stepType?.[0]?.__component ?? null;
}

export function stepTypeFromStepType(
  stepType: StepStepType[] | undefined,
): StepTypeKebabCase | null {
  return componentNameKebabCase(stepComponentFromStepType(stepType) ?? '');
}

const documentStepRepo = ref<Item<Step>>();

function getDocumentStepRepo(documentId: string) {
  documentStepRepo.value = stepRepo
    .withAllRecursive()
    .where('documentId', documentId)
    .first();
}

const LOAD_TIMEOUT_MS = 15000; // 15 seconds
const TIMEOUT_MESSAGE = 'Loading timeout - step data took too long to load';
const UNIVERSAL_KEY = JSON.stringify(UNIVERSAL_STEP_PARAMS);

/**
 * Load state shared by every useStepData instance of one step (StepPage,
 * StepView, useStep, StepTheoryView…), so the view shows the errors and
 * loading of a load another instance started, and concurrent loads of the
 * same request share one GET.
 */
interface StepLoadState {
  isLoading: Ref<boolean>;
  loadError: Ref<string | null>;
  validationErrors: Ref<string[]>;
  /** In-flight loads by request params. */
  inFlight: Map<string, Promise<void>>;
  /** Bumped by every load; only the newest load writes the shared state. */
  generation: number;
}

// Keyed by the active pinia (one per app; a fresh one per test).
const loadStates = new WeakMap<object, Map<string, StepLoadState>>();
const noPinia = {};

function sharedLoadState(documentId: string): StepLoadState {
  const owner = getActivePinia() ?? noPinia;
  let byDoc = loadStates.get(owner);
  if (!byDoc) {
    byDoc = new Map();
    loadStates.set(owner, byDoc);
  }
  let state = byDoc.get(documentId);
  if (!state) {
    state = {
      isLoading: ref(false),
      loadError: ref<string | null>(null),
      validationErrors: ref<string[]>([]),
      inFlight: new Map(),
      generation: 0,
    };
    byDoc.set(documentId, state);
  }
  return state;
}

export const useStepData = (documentId: string) => {
  if (!documentId || documentId.length === 0)
    throw new Error('useStep: documentId is required');

  const state = sharedLoadState(documentId);
  const { isLoading, loadError, validationErrors } = state;

  getDocumentStepRepo(documentId);

  const stepTypeComponent = computed<StepTypeComponnet | null>(() => {
    if (!documentStepRepo.value) return null;
    const { step_type } = documentStepRepo.value;
    if (!step_type) return null;
    return stepComponentFromStepType(step_type as StepStepType[]);
  });

  const stepType = computed<StepTypeKebabCase | null>(() => {
    if (!stepTypeComponent.value) return null;
    return componentNameKebabCase(stepTypeComponent.value);
  });

  function hasLoadedStepContent(): boolean {
    const stepTypeData = documentStepRepo.value?.step_type?.[0];
    if (!stepTypeData) return false;

    const currentStepType = stepType.value;

    if (currentStepType === 'theory') {
      return Array.isArray(stepTypeData.theory_content)
        ? stepTypeData.theory_content.length > 0
        : false;
    }

    if (currentStepType === 'filter') {
      return !!stepTypeData.drill;
    }

    return !!stepTypeData.drill && !!stepTypeData.stack;
  }

  function getStep(): Promise<void> {
    // One GET with every kind's populate: the step kind arrives with its content.
    if (hasLoadedStepContent()) return Promise.resolve();

    // One shared request per step, whoever asks (BUG-8).
    const key = UNIVERSAL_KEY;
    const existing = state.inFlight.get(key);
    if (existing) return existing;

    const load = loadStep(UNIVERSAL_STEP_PARAMS, () => {
      if (state.inFlight.get(key) === load) state.inFlight.delete(key);
    });
    state.inFlight.set(key, load);
    return load;
  }

  async function loadStep(
    requestParams: Record<string, unknown>,
    release: () => void,
  ) {
    const generation = ++state.generation;
    const isNewest = () => generation === state.generation;
    isLoading.value = true;
    loadError.value = null;
    validationErrors.value = [];

    let timedOut = false;
    const loadTimeout = window.setTimeout(() => {
      timedOut = true;
      // Let a later getStep start a fresh request.
      release();
      if (!isNewest()) return;
      loadError.value = TIMEOUT_MESSAGE;
      isLoading.value = false;
    }, LOAD_TIMEOUT_MS);

    try {
      // Use retry logic for fetching step data
      await executeWithRetry(
        async () => {
          const { execute, data, error } = useStrapiGet(
            `steps/${documentId}`,
            requestParams,
          );

          await execute();

          // Keep the original error (code / response) for retryable detection.
          if (error.value) {
            throw error.value instanceof Error
              ? error.value
              : Object.assign(new Error('Failed to fetch step data'), {
                  cause: error.value,
                });
          }

          if (!data.value?.data) {
            throw new Error('No step data received from server');
          }

          stepRepo.save(data.value.data as Step);
          getDocumentStepRepo(documentId);
        },
        {
          maxRetries: 3,
          initialDelayMs: 1000,
          onRetry: (attempt, error) => {
            console.warn(
              `Retrying step fetch (attempt ${attempt}/3):`,
              error.message,
            );
          },
        },
      );

      // A newer load owns the state now (the step itself is saved above).
      if (!isNewest()) return;

      // A late success clears the timeout error.
      if (timedOut && loadError.value === TIMEOUT_MESSAGE) {
        loadError.value = null;
      }

      // Validate step content after loading. Validation errors are shown in
      // their own banner, not as a load error.
      if (documentStepRepo.value) {
        const validation = validateStepContent(
          documentStepRepo.value,
          stepType.value,
        );
        if (!validation.isValid) {
          validationErrors.value = validation.errors;
          console.error('Step validation failed:', validation.errors);
        }
        if (validation.warnings.length > 0) {
          console.warn('Step validation warnings:', validation.warnings);
        }
      }
    } catch (error) {
      const err = error as Error;
      console.error('Failed to load step:', err);
      if (!isNewest()) return;

      // Provide user-friendly error message
      if (isRetryableError(error)) {
        loadError.value =
          'Network error loading step. Please check your connection and try again.';
      } else {
        loadError.value = err.message || 'Failed to load step data';
      }
    } finally {
      release();
      clearTimeout(loadTimeout);
      if (isNewest()) isLoading.value = false;
    }
  }

  return {
    isLoading,
    loadError,
    validationErrors,
    documentStepRepo,
    getStep,
    stepTypeComponent,
    stepType,
  };
};

/** Adapter: a user-step row seen through the shared list-filter shape. */
export function toFilterableStepItem(userStep: {
  step?: { step_name?: string | null } | null;
  start_date?: string | null;
  completion_date?: string | null;
  score?: number | null;
  time_accumulator?: number | null;
}): FilterableListItem {
  return {
    name: userStep.step?.step_name ?? '',
    startDate: userStep.start_date,
    completionDate: userStep.completion_date,
    score: userStep.score,
    timeAccumulator: userStep.time_accumulator,
  };
}
